import React, { useState, useEffect } from 'react';
import { resolveImageUrl, getFallbackByCategory, ASSET_IMAGES } from '../assets/images';

interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
  fallbackCategory?: string;
  alt: string;
  className?: string;
}

export const SafeImage: React.FC<SafeImageProps> = ({
  src,
  fallbackCategory,
  alt,
  className = '',
  onError,
  ...props
}) => {
  const [currentSrc, setCurrentSrc] = useState<string>(() => resolveImageUrl(src, fallbackCategory));
  const [hasError, setHasError] = useState(false);
  const [stage, setStage] = useState<number>(0);

  useEffect(() => {
    setCurrentSrc(resolveImageUrl(src, fallbackCategory));
    setHasError(false);
    setStage(0);
  }, [src, fallbackCategory]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (stage === 0) {
      // Step 1: Attempt the bundled Vite asset for this category
      const fallback = getFallbackByCategory(fallbackCategory);
      if (fallback && fallback !== currentSrc) {
        setStage(1);
        setCurrentSrc(fallback);
        return;
      }
    }

    if (stage <= 1) {
      // Step 2: Attempt standard hero water plant asset
      if (ASSET_IMAGES.heroWater && currentSrc !== ASSET_IMAGES.heroWater) {
        setStage(2);
        setCurrentSrc(ASSET_IMAGES.heroWater);
        return;
      }
    }

    // Step 3: All image URLs failed, show styled SVG fallback
    setHasError(true);
    if (onError) {
      onError(e);
    }
  };

  if (hasError) {
    return (
      <div
        className={`flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-slate-300 p-4 select-none ${className}`}
        role="img"
        aria-label={alt}
      >
        <svg
          className="w-10 h-10 text-sky-400 mb-2 opacity-80"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
          />
        </svg>
        <span className="text-xs font-semibold text-center text-slate-200 line-clamp-1">{alt}</span>
        <span className="text-[10px] text-sky-400 font-medium tracking-wide uppercase mt-0.5">
          Saints Tech Certified
        </span>
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={handleError}
      className={className}
      {...props}
    />
  );
};
